import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles, Building2, Hammer, ClipboardList } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

// Fallback data in case CMS fetch fails or is loading
const FALLBACK_SERVICES = [
  {
    id: 'erhvervsrengoering',
    slug: 'professionel-erhvervsrengoering',
    title: { 
      da: 'Professionel Erhvervsrengøring',
      en: 'Professional Commercial Cleaning'
    },
    category: { 
      da: 'Erhvervsrengøring',
      en: 'Commercial Cleaning'
    },
    description: { 
      da: 'Vi tilbyder skræddersyet erhvervsrengøring til virksomheder og institutioner, der ønsker rene, sunde og præsentable omgivelser.',
      en: 'We offer tailored commercial cleaning for businesses and institutions who want clean, healthy, and presentable surroundings.'
    },
    image: ['https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/e27330cb-7062-441b-be1b-7a6f436b3c9b.jpg'],
    details: { 
      da: '<p>Et sundt arbejdsmiljø starter med professionel renlighed. Vi tilpasser vores rengøring efter jeres specifikke behov og arbejdstider, så jeres hverdag forstyrres mindst muligt.</p><ul><li>Kontorer & administration</li><li>Tandklinikker & dyreklinikker</li><li>Restauranter & cafeer</li><li>Hospitaler & sundhedscentre</li><li>Institutioner & skoler</li><li>Saloner & svømmehaller</li><li>Banker & større virksomheder</li></ul>',
      en: '<p>A healthy working environment starts with professional cleanliness. We adapt our cleaning to your specific needs and working hours, so your daily routine is disrupted as little as possible.</p><ul><li>Offices & administration</li><li>Dental & veterinary clinics</li><li>Restaurants & cafes</li><li>Hospitals & health centers</li><li>Institutions & schools</li><li>Salons & swimming pools</li><li>Banks & larger corporations</li></ul>'
    }
  },
  {
    id: 'specialrengoering',
    slug: 'skraeddersyet-specialrengoering',
    title: { 
      da: 'Skræddersyet Specialrengøring',
      en: 'Tailored Specialty Cleaning'
    },
    category: { 
      da: 'Specialrengøring',
      en: 'Specialty Cleaning'
    },
    description: { 
      da: 'Når den almindelige rengøring ikke er nok. Vi klarer alt fra dybdegående hovedrengøring til flytte- og sommerhusrengøring.',
      en: 'When regular cleaning is not enough. We handle everything from deep cleaning to move-out and holiday home cleaning.'
    },
    image: ['https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/67035ddd-e45a-4204-8a37-bd91b9abbb9d.jpg'],
    details: { 
      da: '<p>Nogle opgaver kræver ekstra fokus og specialiserede metoder. Vores erfarne team rykker ud til alle typer specialopgaver med det rette udstyr og miljøvenlige produkter.</p><ul><li><strong>Hovedrengøring:</strong> Dybdegående rengøring af hele ejendommen.</li><li><strong>Flytterengøring:</strong> Gør boligen eller kontoret klar til de næste beboere.</li><li><strong>Slutrengøring:</strong> Professionel rengøring efter renovering eller nybyg.</li><li><strong>Sommerhusrengøring:</strong> Sikring af en indbydende og ren feriebolig til gæster.</li></ul>',
      en: '<p>Some tasks require extra focus and specialized methods. Our experienced team handles all types of special tasks with the right equipment and eco-friendly products.</p><ul><li><strong>Deep Cleaning:</strong> In-depth cleaning of the entire property.</li><li><strong>Move-out Cleaning:</strong> Preparing the home or office for the next occupants.</li><li><strong>Post-construction Cleaning:</strong> Professional cleaning after renovation or new build.</li><li><strong>Holiday Home Cleaning:</strong> Ensuring an inviting and clean holiday home for guests.</li></ul>'
    }
  },
  {
    id: 'byggepladsservice',
    slug: 'effektiv-byggepladsservice',
    title: { 
      da: 'Effektiv Byggepladsservice',
      en: 'Effective Construction Site Service'
    },
    category: { 
      da: 'Byggepladsservice',
      en: 'Construction Site Service'
    },
    description: { 
      da: 'Vi sørger for løbende oprydning af byggerod, grundig byggerengøring, skurevognrengøring og professionel service på byggepladser.',
      en: 'We handle ongoing construction cleanup, thorough post-construction cleaning, trailer cleaning, and professional service on sites.'
    },
    image: ['https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/6da78284-877d-4c6a-8b15-61015b2c66e6.jpg'],
    details: { 
      da: '<p>En sikker og effektiv byggeplads kræver struktur og renlighed. Vi hjælper med at holde byggepladsen ryddelig, så håndværkerne kan fokusere på deres arbejde i trygge rammer.</p><ul><li>Oprydning af byggepladser</li><li>Løbende oprydning af byggerod og affaldssortering</li><li>Grundig byggerengøring inden aflevering</li><li>Professionel skurevognrengøring (omklædning, kantine og bad)</li><li>Serviceopgaver og praktisk hjælp på byggepladsen</li><li>Sikring af et sundt og sikkert arbejdsmiljø</li></ul>',
      en: '<p>A safe and efficient construction site requires structure and cleanliness. We help keep the site tidy so the craftsmen can focus on their work in safe surroundings.</p><ul><li>Construction site cleanup</li><li>Ongoing debris cleanup and waste sorting</li><li>Thorough post-construction cleaning before handover</li><li>Professional trailer cleaning (changing rooms, canteen, and bath)</li><li>Service tasks and practical help on site</li><li>Ensuring a healthy and safe working environment</li></ul>'
    }
  }
]

export default function Services() {
  const { language, t, linkTo } = useLanguage()
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services')
        if (response.ok) {
          const data = await response.json()
          if (Array.isArray(data) && data.length > 0) {
            // Filter to only keep the three requested services (both Danish and English slugs)
            const allowedSlugs = [
              'skraeddersyet-specialrengoering',
              'professionel-erhvervsrengoering',
              'effektiv-byggepladsservice',
              'tailored-specialty-cleaning',
              'professional-commercial-cleaning',
              'effective-construction-site-service'
            ]
            const allowedIds = [
              'specialrengoering',
              'erhvervsrengoering',
              'byggepladsservice'
            ]
            const filtered = data.filter(service => {
              const slugDa = service.slug?.da || service.slug || service.id
              const slugEn = service.slug?.en || ''
              const id = service.id
              return allowedSlugs.includes(slugDa) || allowedSlugs.includes(slugEn) || allowedIds.includes(id)
            })
            setServices(filtered.length > 0 ? filtered : FALLBACK_SERVICES)
          } else {
            setServices(FALLBACK_SERVICES)
          }
        } else {
          setServices(FALLBACK_SERVICES)
        }
      } catch (err) {
        console.error('Error fetching services:', err)
        setServices(FALLBACK_SERVICES)
      } finally {
        setLoading(false)
      }
    }

    fetchServices()
  }, [])

  return (
    <section id="ydelser" className="relative bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
          <h2 className="font-marcellus text-2xl sm:text-4xl md:text-5xl font-light tracking-[0.08em] text-[#2C302E] leading-tight mb-4 uppercase">
            {t('services.title')} <br className="hidden sm:inline" />
            <span className="font-normal text-[#a78458]">{t('services.title_gold')}</span> {t('services.title_end')}
          </h2>
          <p className="text-sm sm:text-base text-[#2C302E]/70 leading-relaxed max-w-xl mx-auto">
            {t('services.desc')}
          </p>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#a78458] border-t-transparent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {services.map((service) => {
              const titleText = service.title?.[language] || service.title?.da || service.title || ''
              const descText = service.description?.[language] || service.description?.da || service.description || ''
              const imageUrl = Array.isArray(service.image) ? service.image[0] : service.image
              const serviceSlug = service.slug?.[language] || service.slug?.da || service.slug || service.id

              return (
                <div
                  key={service.id || serviceSlug}
                  className="group relative flex flex-col bg-white rounded-none overflow-hidden border border-[#E6DFD3] transition-all duration-300 hover:shadow-xl hover:-translate-y-1.5"
                >
                  {/* Image Container */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={titleText}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C302E]/60 via-transparent to-transparent opacity-80" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-6 flex flex-col justify-between">
                    <div>
                      <h3 className="font-marcellus text-lg font-light text-[#2C302E] mb-3 group-hover:text-[#a78458] transition-colors uppercase tracking-wider">
                        {titleText}
                      </h3>
                      {descText.startsWith('<') ? (
                        <div
                          className="text-sm text-[#2C302E]/70 leading-relaxed mb-4"
                          dangerouslySetInnerHTML={{ __html: descText }}
                        />
                      ) : (
                        <p className="text-sm text-[#2C302E]/70 leading-relaxed mb-4">
                          {descText}
                        </p>
                      )}
                    </div>

                    <Link
                      to={linkTo(`/ydelser/${serviceSlug}`)}
                      className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider text-[#a78458] uppercase group/btn mt-auto"
                    >
                      <span>{language === 'da' ? 'Læs mere & se detaljer' : 'Read more & see details'}</span>
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Quick Trust Banner */}
        <div className="mt-12 bg-[#a78458]/10 rounded-none p-6 sm:p-8 border border-[#a78458]/20 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-none bg-white flex items-center justify-center text-[#a78458] shadow-sm">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-marcellus text-lg font-light text-[#2C302E] uppercase tracking-wider">
                {language === 'da' ? 'Skræddersyede aftaler' : 'Tailored agreements'}
              </h4>
              <p className="text-xs sm:text-sm text-[#2C302E]/70">
                {language === 'da' 
                  ? 'Vi sammensætter en rengøringsplan, der passer præcis til jeres behov.'
                  : 'We compile a cleaning plan that fits exactly to your needs.'}
              </p>
            </div>
          </div>
          <Link
            to={linkTo('/kontakt')}
            className="rounded-none bg-[#a78458] px-6 py-3 text-xs font-semibold tracking-wider text-[#FAF8F5] uppercase transition-all duration-300 hover:bg-[#917047] hover:shadow-md"
          >
            {t('contact.form_submit')}
          </Link>
        </div>
      </div>
    </section>
  )
}
